/**
 * FreeBonusTracker Component
 *
 * Shows progress toward the free weekly ticket available to ALL users.
 * 7-day login streak grants 1 free jackpot ticket.
 */

import { memo } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

interface FreeBonusTrackerProps {
  streak: number;
  className?: string;
}

export const FreeBonusTracker = memo(function FreeBonusTracker({
  streak,
  className = '',
}: FreeBonusTrackerProps) {
  const { t } = useTranslation();
  const daysRemaining = Math.max(0, 7 - streak);
  const isComplete = streak >= 7;

  return (
    <div className={`p-4 rounded-lg bg-green-500/10 border border-green-500/30 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-green-400 flex items-center gap-2">
          <span role="img" aria-label="gift">
            {'\u{1F381}'}
          </span>{' '}
          {t('jackpot.freeWeeklyTicket', 'Free Weekly Ticket')}
        </h4>
        <span className="text-xs text-slate-400">
          {t('jackpot.availableToEveryone', 'Available to everyone!')}
        </span>
      </div>

      {/* 7-day streak indicator */}
      <div className="flex items-center gap-1 mb-3">
        {[...Array(7)].map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-2 rounded-full transition-all duration-300
                       ${i < streak ? 'bg-green-500' : 'bg-slate-700'}`}
          />
        ))}
      </div>

      {/* Progress text */}
      <p className="text-sm text-slate-400">
        {streak === 0 ? (
          t('jackpot.loginToStartStreak', 'Login daily to start your streak!')
        ) : isComplete ? (
          <span className="text-green-400 font-medium flex items-center gap-1">
            <span role="img" aria-label="check">
              {'\u2713'}
            </span>{' '}
            {t('jackpot.streakComplete', 'Streak complete! Ticket claimed!')}
          </span>
        ) : (
          <>
            {streak}/7 {t('jackpot.days', 'days')} -{' '}
            <span className="text-green-400">
              {daysRemaining} {t('jackpot.moreToGo', 'more to go!')}
            </span>
          </>
        )}
      </p>

      {/* Warning */}
      {!isComplete && (
        <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
          <span role="img" aria-label="warning">
            {'\u26A0\uFE0F'}
          </span>{' '}
          {t('jackpot.streakWarning', 'Missing a day resets progress')}
        </p>
      )}
    </div>
  );
});
